import React, { useState, useRef } from 'react'

export default function GalleryUploadModal({
  isOpen = false,
  onClose,
  onSave,
  isSaving = false,
}) {
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState('')
  const [caption, setCaption] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef(null)

  if (!isOpen) return null

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      processFile(file)
    }
  }

  const processFile = (file) => {
    if (!file.type.startsWith('image/')) {
      appToast.warning('Please select an image file (PNG, JPG, JPEG, WEBP).')
      return
    }
    setSelectedFile(file)
    const reader = new FileReader()
    reader.onload = () => {
      setPreviewUrl(reader.result)
    }
    reader.readAsDataURL(file)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) {
      processFile(file)
    }
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!previewUrl) return
    const success = await onSave({ imageUrl: previewUrl, caption })
    if (success) {
      setSelectedFile(null)
      setPreviewUrl('')
      setCaption('')
    }
  }

  const handleClose = () => {
    setSelectedFile(null)
    setPreviewUrl('')
    setCaption('')
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 sm:p-8 shadow-2xl">
        {/* Close Button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-on-surface-variant hover:bg-surface-container-high transition cursor-pointer"
        >
          <span className="material-symbols-outlined text-lg">close</span>
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 border-b border-outline-variant/15 pb-4 mb-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-xl">add_photo_alternate</span>
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-on-surface">
              Upload New Picture
            </h3>
            <p className="text-xs text-on-surface-variant">
              Add photos to the institutional gallery
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-3.5 mb-5 flex items-start gap-2.5 text-xs text-on-surface-variant leading-relaxed">
          <span className="material-symbols-outlined text-primary text-base shrink-0 mt-0.5">
            info
          </span>
          <span>
            These pictures are visible to students &amp; parents when they log-in on the MyInstitute mobile app &amp; web portal.
          </span>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Drag and drop zone */}
          {!previewUrl ? (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-8 transition cursor-pointer ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-outline-variant/40 bg-surface-container-low/50 hover:border-primary/60 hover:bg-surface-container-low'
              }`}
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-container-high text-primary">
                <span className="material-symbols-outlined text-2xl">cloud_upload</span>
              </div>
              <div className="text-center">
                <p className="text-xs font-bold text-on-surface">
                  Click to select photo or drag &amp; drop here
                </p>
                <p className="text-[11px] text-on-surface-variant mt-1">
                  Supports PNG, JPG, JPEG, WEBP (Max 1000px × 510px auto-optimized)
                </p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          ) : (
            <div className="relative overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-high">
              <img
                src={previewUrl}
                alt="Upload preview"
                className="h-52 w-full object-cover"
              />
              <button
                type="button"
                onClick={() => {
                  setPreviewUrl('')
                  setSelectedFile(null)
                }}
                className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black transition cursor-pointer"
              >
                <span className="material-symbols-outlined text-sm">delete</span>
              </button>
            </div>
          )}

          {/* Caption Input */}
          <div>
            <label className="text-xs font-black uppercase tracking-wider text-on-surface-variant block mb-1.5">
              Picture Caption / Title
            </label>
            <input
              type="text"
              placeholder="e.g. Annual Sports Meet 2026 / Science Exhibition"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              className="h-10 w-full rounded-xl border border-outline-variant/30 bg-surface-container-low px-3.5 text-xs text-on-surface outline-none focus:border-primary"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-outline-variant/15 mt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!previewUrl || isSaving}
              className="flex items-center gap-1.5 rounded-xl bg-primary px-5 py-2 text-xs font-extrabold text-on-primary shadow-xs hover:bg-primary/90 transition cursor-pointer disabled:opacity-50"
            >
              {isSaving ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-base">save</span>
                  <span>Save Picture</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
